import { motion } from "framer-motion";
import { ArrowRight, Sparkles, PlayCircle } from "lucide-react";
import useSiteConfig from "@/hooks/useSiteConfig";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/220ed046-f4a9-49c2-94d5-bdf9747703f3/images/ff35fd7fc0a1ae4cd26db8df1e4a1d15d8148c57b45e5628cb3ecacab613dc50.png";

export default function Hero() {
  const { config } = useSiteConfig();
  const h = config.hero;
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[88vh] md:min-h-screen flex items-center pt-24 pb-14 md:pt-32 md:pb-24 overflow-hidden noise"
    >
      {/* Background image with overlay */}
      <div className="absolute inset-0 -z-10">
        <img
          src={HERO_BG}
          alt="Dark cinematic backdrop"
          className="h-full w-full object-cover opacity-60 hero-dark-bg"
        />
        <div className="absolute inset-0 hero-dark-overlay bg-gradient-to-b from-[#050505]/40 via-[#050505]/70 to-[#050505]" />
        <div className="absolute inset-0 grid-bg radial-fade opacity-50" />
      </div>

      {/* Floating orbs */}
      <motion.div
        animate={{ y: [0, -20, 0], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-32 left-[8%] h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl"
        aria-hidden
      />
      <motion.div
        animate={{ y: [0, 24, 0], opacity: [0.4, 0.6, 0.4] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="absolute bottom-24 right-[10%] h-80 w-80 rounded-full bg-purple-600/20 blur-3xl"
        aria-hidden
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-[11px] font-mono-acc uppercase tracking-[0.22em] text-cyan-300 mb-8"
          data-testid="hero-overline"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-cyan-400 pulse-dot" />
          {h.overline}
          <Sparkles size={12} className="text-purple-300" />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-clash text-[40px] sm:text-6xl lg:text-[88px] font-semibold leading-[1.04] sm:leading-[1.02] tracking-tight sm:tracking-tighter max-w-5xl"
          data-testid="hero-headline"
        >
          {h.headlineLead}{" "}
          <span className="text-gradient-neon">{h.headlineHighlight}</span>{" "}
          <br className="hidden md:block" />
          {h.headlineTail}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-5 sm:mt-7 max-w-2xl text-[15px] sm:text-base md:text-lg text-zinc-300/90 leading-relaxed font-light"
          data-testid="hero-subheadline"
        >
          {h.subheadline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <a
            href={config.contact.bookingUrl || "#contact"}
            data-testid="hero-cta-strategy"
            className="group relative inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-7 py-3.5 text-sm font-medium hover:scale-[1.03] active:scale-[0.98] transition-transform shadow-[0_0_40px_-10px_rgba(0,229,255,0.5)]"
          >
            Book Strategy Call
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
          </a>
          <a
            href="#audit"
            data-testid="hero-cta-audit"
            className="inline-flex items-center justify-center gap-2 rounded-full glass-strong px-7 py-3.5 text-sm font-medium text-white hover:bg-white/10 transition-colors"
          >
            Get Free Audit
          </a>
          <a
            href="#case-studies"
            data-testid="hero-cta-case-studies"
            className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors group"
          >
            <PlayCircle size={18} className="text-cyan-400" />
            View Case Studies
            <span className="transition-transform group-hover:translate-x-0.5">→</span>
          </a>
        </motion.div>

        {/* Mini KPI row */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="mt-12 sm:mt-20 grid grid-cols-2 md:grid-cols-4 gap-px rounded-2xl overflow-hidden glass max-w-4xl"
          data-testid="hero-mini-stats"
        >
          {[
            ["AED 85M+", "Revenue Generated"],
            ["320%", "Avg. ROAS Lift"],
            ["14+", "Countries Served"],
            ["92%", "Client Retention"],
          ].map(([n, l]) => (
            <div key={l} className="bg-[#0a0a0c] px-5 py-5">
              <div className="font-clash text-2xl md:text-3xl font-semibold text-white">{n}</div>
              <div className="mt-1 text-[11px] font-mono-acc uppercase tracking-[0.18em] text-zinc-400">
                {l}
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] font-mono-acc tracking-[0.3em] text-zinc-500 flex items-center gap-2 z-10">
        <span className="h-px w-8 bg-zinc-700" />
        SCROLL
        <span className="h-px w-8 bg-zinc-700" />
      </div>
    </section>
  );
}
